import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';

// HTTP Status constants
const HTTP_FORBIDDEN = 403;

import { 
    Target, ArrowLeft, ArrowRight, Plus, Trash2, Sparkles, 
    FileText, Download, Loader2, Check, Gamepad2, Trophy,
    Monitor, Smartphone, Tv, Share2, Copy, Link as LinkIcon,
    Import, User, Mail, Phone, MapPin, Globe, Linkedin,
    ThumbsUp, ThumbsDown, Briefcase, Crown, Lock, Wand2, FileEdit,
    GraduationCap, AlertCircle, BarChart3, HelpCircle, Calendar,
    DollarSign, TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
    { value: 'PC', label: 'PC Gaming', icon: Monitor },
    { value: 'Console', label: 'Console (PS/Xbox/Switch)', icon: Tv },
    { value: 'Mobile', label: 'Mobile Gaming', icon: Smartphone }
];

const TEMPLATES = [
    { value: 'modern', label: 'Modern', description: 'Clean with accent colors' },
    { value: 'classic', label: 'Classic', description: 'Traditional serif style' },
    { value: 'minimal', label: 'Minimal', description: 'Simple and elegant' }
];

export default function ResumeBuilder() {
    const navigate = useNavigate();
    const { resumeId } = useParams();
    const { getAuthHeaders, user, userTier } = useAuth();
    
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('modern');
    const [steamDialogOpen, setSteamDialogOpen] = useState(false);
    const [steamId, setSteamId] = useState('');
    const [steamApiKey, setSteamApiKey] = useState('');
    const [importingGames, setImportingGames] = useState(false);
    
    // Feedback dialog state
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    
    // Job tailoring state (Premium)
    const [tailorDialogOpen, setTailorDialogOpen] = useState(false);
    const [jobDescription, setJobDescription] = useState('');
    const [tailoring, setTailoring] = useState(false);
    const [tailorResult, setTailorResult] = useState(null);
    
    // Cover letter state (Premium)
    const [coverLetterDialogOpen, setCoverLetterDialogOpen] = useState(false);
    const [coverLetterJobDesc, setCoverLetterJobDesc] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [hiringManager, setHiringManager] = useState('');
    const [generatingCoverLetter, setGeneratingCoverLetter] = useState(false);
    const [coverLetterResult, setCoverLetterResult] = useState(null);
    
    // Skill Gap Detector state (Free+)
    const [skillGapDialogOpen, setSkillGapDialogOpen] = useState(false);
    const [analyzingGaps, setAnalyzingGaps] = useState(false);
    const [skillGapResult, setSkillGapResult] = useState(null);
    
    // Achievement Scoring state (Pro+)
    const [achievementScoreDialogOpen, setAchievementScoreDialogOpen] = useState(false);
    const [scoringAchievements, setScoringAchievements] = useState(false);
    const [achievementScoreResult, setAchievementScoreResult] = useState(null);
    
    // Interview Predictor state (Premium)
    const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
    const [interviewJobDesc, setInterviewJobDesc] = useState('');
    const [predictingInterview, setPredictingInterview] = useState(false);
    const [interviewResult, setInterviewResult] = useState(null);
    
    // Job Recommendations state (All tiers)
    const [jobRecsDialogOpen, setJobRecsDialogOpen] = useState(false);
    const [loadingJobRecs, setLoadingJobRecs] = useState(false);
    const [jobRecsResult, setJobRecsResult] = useState(null);
    
    // Education state
    const [currentEducation, setCurrentEducation] = useState({
        institution: '',
        degree: '',
        field_of_study: '',
        start_year: '',
        end_year: '',
        is_current: false,
        achievements: []
    });
    const [newEduAchievement, setNewEduAchievement] = useState('');
    
    // Tones state
    const [availableTones, setAvailableTones] = useState([]);
    const [selectedTone, setSelectedTone] = useState('professional');
    
    const [resume, setResume] = useState({
        title: '',
        target_job: '',
        games: [],
        skills: [],
        summary: '',
        is_public: false,
        share_token: '',
        tone: 'professional',
        education: [],
        personal_info: {
            full_name: '',
            email: '',
            phone: '',
            location: '',
            linkedin: '',
            website: '',
            date_of_birth: ''
        }
    });
    
    const [currentGame, setCurrentGame] = useState({
        name: '',
        platform: '',
        level: '',
        rank: '',
        hours_played: '',
        achievements: [],
        play_style: {
            mode: '',
            playstyle: '',
            primary_role: '',
            decision_style: '',
            focus_area: '',
            biggest_challenge: '',
            how_improved: '',
            play_frequency: '',
            performance_style: ''
        }
    });
    
    const [newAchievement, setNewAchievement] = useState('');
    const [showPlayStyleForm, setShowPlayStyleForm] = useState(false);
    const fetchTones = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/tones`, getAuthHeaders());
            setAvailableTones(response.data.tones);
        } catch (error) {
            console.error('Failed to fetch tones:', error);
            // Will use default tones
        }
    }, [getAuthHeaders]);

    const fetchResume = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/resumes/${resumeId}`, getAuthHeaders());
            setResume({
                ...response.data,
                personal_info: response.data.personal_info || {
                    full_name: '',
                    email: '',
                    phone: '',
                    location: '',
                    linkedin: '',
                    website: '',
                    date_of_birth: ''
                }
            });
            setSelectedTone(response.data.tone || 'professional');
            if (response.data.skills?.length > 0) {
                setStep(3);
            } else if (response.data.games?.length > 0) {
                setStep(2);
            }
        } catch (error) {
            toast.error('Failed to load resume');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    }, [resumeId, getAuthHeaders, navigate]);

    useEffect(() => {
        fetchTones();
        if (resumeId) {
            fetchResume();
        } else if (user) {
            // Pre-fill personal info for new resumes
            setResume(prev => ({
                ...prev,
                personal_info: {
                    ...prev.personal_info,
                    full_name: user.username || '',
                    email: user.email || ''
                }
            }));
        }
    }, [resumeId, user, fetchTones, fetchResume]);

    const saveResume = async (data = resume) => {
        setSaving(true);
        try {
            if (resumeId) {
                const response = await axios.put(`${API}/resumes/${resumeId}`, data, getAuthHeaders());
                setResume(response.data);
                return response.data;
            } else {
                const response = await axios.post(`${API}/resumes`, data, getAuthHeaders());
                navigate(`/builder/${response.data.id}`, { replace: true });
                return response.data;
            }
        } catch (error) {
            toast.error('Failed to save resume');
            throw error;
        } finally {
            setSaving(false);
        }
    };

    const addGame = () => {
        if (!currentGame.name || !currentGame.platform) {
            toast.error('Please enter game name and platform');
            return;
        }
        
        // Clean up play_style - only include non-empty values
        const cleanPlayStyle = Object.fromEntries(
            Object.entries(currentGame.play_style || {}).filter(([_, v]) => v && v !== '')
        );
        
        const gameToAdd = {
            ...currentGame,
            hours_played: currentGame.hours_played ? parseInt(currentGame.hours_played) : null,
            play_style: Object.keys(cleanPlayStyle).length > 0 ? cleanPlayStyle : null
        };
        
        setResume(prev => ({
            ...prev,
            games: [...prev.games, gameToAdd]
        }));
        
        setCurrentGame({
            name: '',
            platform: '',
            level: '',
            rank: '',
            hours_played: '',
            achievements: [],
            play_style: {
                mode: '',
                playstyle: '',
                primary_role: '',
                decision_style: '',
                focus_area: '',
                biggest_challenge: '',
                how_improved: '',
                play_frequency: '',
                performance_style: ''
            }
        });
        setShowPlayStyleForm(false);
        
        toast.success(`${gameToAdd.name} added!`);
    };

    const removeGame = (index) => {
        setResume(prev => ({
            ...prev,
            games: prev.games.filter((_, i) => i !== index)
        }));
    };

    const addAchievement = () => {
        if (!newAchievement.trim()) return;
        setCurrentGame(prev => ({
            ...prev,
            achievements: [...prev.achievements, newAchievement.trim()]
        }));
        setNewAchievement('');
    };

    const removeAchievement = (index) => {
        setCurrentGame(prev => ({
            ...prev,
            achievements: prev.achievements.filter((_, i) => i !== index)
        }));
    };

    const generateSkills = async (toneOverride = null) => {
        if (resume.games.length === 0) {
            toast.error('Add at least one game first');
            return;
        }
        
        const toneToUse = toneOverride || selectedTone;
        
        setGenerating(true);
        try {
            const response = await axios.post(`${API}/generate/skills`, {
                games: resume.games,
                target_job: resume.target_job,
                tone: toneToUse
            }, getAuthHeaders());
            
            const updatedResume = { ...resume, skills: response.data.skills, tone: toneToUse };
            await saveResume(updatedResume);
            setResume(updatedResume);
            toast.success('Skills generated successfully!');
            setStep(3);
        } catch (error) {
            toast.error('Failed to generate skills. Try again.');
        } finally {
            setGenerating(false);
        }
    };

    const generateSummary = async () => {
        if (resume.skills.length === 0) {
            toast.error('Generate skills first');
            return;
        }
        
        setGenerating(true);
        try {
            const response = await axios.post(`${API}/generate/summary`, {
                games: resume.games,
                skills: resume.skills,
                target_job: resume.target_job,
                tone: selectedTone
            }, getAuthHeaders());
            
            const updatedResume = { ...resume, summary: response.data.summary };
            await saveResume(updatedResume);
            setResume(updatedResume);
            toast.success('Summary generated!');
        } catch (error) {
            toast.error('Failed to generate summary. Try again.');
        } finally {
            setGenerating(false);
        }
    };

    const handleToneChange = async (newTone) => {
        // Check if tone is available for user's tier
        const tone = availableTones.find(t => t.id === newTone);
        if (tone && !tone.available) {
            toast.error(`Upgrade to ${tone.tier_required} to use this tone`);
            return;
        }
        
        setSelectedTone(newTone);
        
        // If we have skills, regenerate them with the new tone
        if (resume.skills.length > 0 && resumeId) {
            toast.info('Regenerating content with new tone...');
            await generateSkills(newTone);
        }
    };

    const handleStep1Submit = async () => {
        if (!resume.title) {
            toast.error('Please enter a resume title');
            return;
        }
        await saveResume();
        setStep(2);
    };

    const handleStep2Submit = async () => {
        if (resume.games.length === 0) {
            toast.error('Add at least one game');
            return;
        }
        await saveResume();
        await generateSkills();
    };

    const downloadPDF = async () => {
        if (!resumeId) return;
        
        setDownloadingPdf(true);
        try {
            const response = await axios.get(
                `${API}/resumes/${resumeId}/pdf?template=${selectedTemplate}`, 
                {
                    ...getAuthHeaders(),
                    responseType: 'blob'
                }
            );
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${resume.title.replace(/\s+/g, '_')}_resume.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('PDF downloaded!');
            
            // Show feedback dialog after download
            setTimeout(() => {
                setFeedbackDialogOpen(true);
            }, 1000);
        } catch (error) {
            toast.error('Failed to generate PDF');
        } finally {
            setDownloadingPdf(false);
        }
    };

    const submitFeedback = async (status) => {
        setFeedbackSubmitting(true);
        try {
            await axios.post(
                `${API}/resumes/${resumeId}/success?status=${status}`,
                {},
                getAuthHeaders()
            );
            toast.success(status === 'none' ? 'Thanks for your feedback!' : 'Great news! Thanks for letting us know!');
        } catch (error) {
            console.error('Feedback submission failed:', error);
            // Non-critical - don't bother user with error
        } finally {
            setFeedbackSubmitting(false);
            setFeedbackDialogOpen(false);
        }
    };

    const tailorToJob = async () => {
        if (!jobDescription.trim()) {
            toast.error('Please paste a job description');
            return;
        }
        
        setTailoring(true);
        try {
            const response = await axios.post(`${API}/generate/tailor`, {
                resume_id: resumeId,
                job_description: jobDescription
            }, getAuthHeaders());
            
            setTailorResult(response.data);
            toast.success(`Resume tailored! Match score: ${response.data.match_score}%`);
        } catch (error) {
            if (error.response?.status === HTTP_FORBIDDEN) {
                toast.error('Job tailoring is a Premium feature. Upgrade to access!');
            } else {
                toast.error('Failed to tailor resume. Try again.');
            }
        } finally {
            setTailoring(false);
        }
    };

    const applyTailoredContent = async () => {
        if (!tailorResult) return;
        
        const updatedResume = {
            ...resume,
            skills: tailorResult.tailored_skills,
            summary: tailorResult.tailored_summary
        };
        
        await saveResume(updatedResume);
        setResume(updatedResume);
        setTailorDialogOpen(false);
        setTailorResult(null);
        setJobDescription('');
        toast.success('Tailored content applied to your resume!');
    };

    const generateCoverLetter = async () => {
        setGeneratingCoverLetter(true);
        try {
            const response = await axios.post(`${API}/generate/cover-letter`, {
                resume_id: resumeId,
                job_description: coverLetterJobDesc || undefined,
                company_name: companyName || undefined,
                hiring_manager: hiringManager || undefined
            }, getAuthHeaders());
            
            setCoverLetterResult(response.data);
            toast.success('Cover letter generated!');
        } catch (error) {
            if (error.response?.status === HTTP_FORBIDDEN) {
                toast.error('Cover letter generation is a Premium feature. Upgrade to access!');
            } else {
                toast.error('Failed to generate cover letter. Try again.');
            }
        } finally {
            setGeneratingCoverLetter(false);
        }
    };

    const copyCoverLetter = () => {
        if (coverLetterResult?.cover_letter) {
            navigator.clipboard.writeText(coverLetterResult.cover_letter);
            toast.success('Cover letter copied to clipboard!');
        }
    };

    const resetCoverLetterDialog = () => {
        setCoverLetterDialogOpen(false);
        setCoverLetterResult(null);
        setCoverLetterJobDesc('');
        setCompanyName('');
        setHiringManager('');
    };

    // Skill Gap Analysis (Free+)
    const analyzeSkillGaps = async () => {
        setAnalyzingGaps(true);
        try {
            const response = await axios.post(`${API}/analyze/skill-gaps`, {
                resume_id: resumeId
            }, getAuthHeaders());
            
            setSkillGapResult(response.data);
            toast.success('Skill analysis complete!');
        } catch (error) {
            toast.error('Failed to analyze skills. Try again.');
        } finally {
            setAnalyzingGaps(false);
        }
    };

    // Achievement Scoring (Pro+)
    const scoreAchievements = async () => {
        setScoringAchievements(true);
        try {
            const response = await axios.post(`${API}/analyze/achievement-scores`, {
                resume_id: resumeId
            }, getAuthHeaders());
            
            setAchievementScoreResult(response.data);
            toast.success('Achievements scored!');
        } catch (error) {
            if (error.response?.status === HTTP_FORBIDDEN) {
                toast.error('Achievement scoring is a Pro feature. Upgrade to access!');
            } else {
                toast.error('Failed to score achievements. Try again.');
            }
        } finally {
            setScoringAchievements(false);
        }
    };

    // Interview Question Predictor (Premium)
    const predictInterviewQuestions = async () => {
        if (!interviewJobDesc.trim()) {
            toast.error('Please paste a job description');
            return;
        }
        
        setPredictingInterview(true);
        try {
            const response = await axios.post(`${API}/analyze/interview-questions`, {
                resume_id: resumeId,
                job_description: interviewJobDesc
            }, getAuthHeaders());
            
            setInterviewResult(response.data);
            toast.success(`${response.data.question_count} interview questions predicted!`);
        } catch (error) {
            if (error.response?.status === HTTP_FORBIDDEN) {
                toast.error('Interview prediction is a Premium feature. Upgrade to access!');
            } else {
                toast.error('Failed to predict questions. Try again.');
            }
        } finally {
            setPredictingInterview(false);
        }
    };

    const resetInterviewDialog = () => {
        setInterviewDialogOpen(false);
        setInterviewResult(null);
        setInterviewJobDesc('');
    };

    // Job Recommendations (All tiers)
    const getJobRecommendations = async () => {
        setLoadingJobRecs(true);
        try {
            const response = await axios.post(`${API}/analyze/job-recommendations`, {
                resume_id: resumeId
            }, getAuthHeaders());
            
            setJobRecsResult(response.data);
            toast.success('Job recommendations ready!');
        } catch (error) {
            toast.error('Failed to get job recommendations. Try again.');
        } finally {
            setLoadingJobRecs(false);
        }
    };

    // Education functions
    const addEducation = () => {
        if (!currentEducation.institution || !currentEducation.degree) {
            toast.error('Please enter institution and degree');
            return;
        }
        
        const eduToAdd = {
            ...currentEducation,
            start_year: currentEducation.start_year ? parseInt(currentEducation.start_year) : null,
            end_year: currentEducation.end_year ? parseInt(currentEducation.end_year) : null
        };
        
        setResume(prev => ({
            ...prev,
            education: [...(prev.education || []), eduToAdd]
        }));
        
        setCurrentEducation({
            institution: '',
            degree: '',
            field_of_study: '',
            start_year: '',
            end_year: '',
            is_current: false,
            achievements: []
        });
        
        toast.success('Education added!');
    };

    const removeEducation = (index) => {
        setResume(prev => ({
            ...prev,
            education: prev.education.filter((_, i) => i !== index)
        }));
    };

    const addEduAchievement = () => {
        if (!newEduAchievement.trim()) return;
        setCurrentEducation(prev => ({
            ...prev,
            achievements: [...prev.achievements, newEduAchievement.trim()]
        }));
        setNewEduAchievement('');
    };

    const removeEduAchievement = (index) => {
        setCurrentEducation(prev => ({
            ...prev,
            achievements: prev.achievements.filter((_, i) => i !== index)
        }));
    };

    const togglePublic = async () => {
        const newIsPublic = !resume.is_public;
        try {
            const response = await axios.put(
                `${API}/resumes/${resumeId}`,
                { is_public: newIsPublic },
                getAuthHeaders()
            );
            setResume(response.data);
            toast.success(newIsPublic ? 'Resume is now public!' : 'Resume is now private');
        } catch (error) {
            toast.error('Failed to update visibility');
        }
    };

    const copyShareLink = () => {
        const shareUrl = `${BASE_URL}/share/${resume.share_token}`;
        navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied!');
    };

    const importSteamGames = async () => {
        if (!steamId) {
            toast.error('Please enter your Steam ID');
            return;
        }
        
        setImportingGames(true);
        try {
            const response = await axios.post(`${API}/import/steam`, {
                steam_id: steamId,
                api_key: steamApiKey || undefined
            }, getAuthHeaders());
            
            const importedGames = response.data.games;
            setResume(prev => ({
                ...prev,
                games: [...prev.games, ...importedGames]
            }));
            
            toast.success(`Imported ${importedGames.length} games from Steam!`);
            setSteamDialogOpen(false);
            setSteamId('');
            setSteamApiKey('');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to import Steam games';
            toast.error(message);
        } finally {
            setImportingGames(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#09090b]">
            {/* Header */}
            <header className="border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Dashboard
                    </Link>
                    <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        <span className="font-bold">Resume Builder</span>
                    </div>
                </div>
            </header>

            {/* Progress Steps */}
            <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="flex items-center justify-center gap-4 mb-12">
                    {[
                        { num: 1, label: 'Basic Info' },
                        { num: 2, label: 'Add Games' },
                        { num: 3, label: 'Preview' }
                    ].map((s, i) => (
                        <div key={s.num} className="flex items-center">
                            <div 
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                                    step >= s.num 
                                        ? 'bg-primary text-primary-foreground' 
                                        : 'bg-secondary text-muted-foreground'
                                }`}
                            >
                                {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                            </div>
                            <span className={`ml-2 text-sm hidden sm:block ${step >= s.num ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {s.label}
                            </span>
                            {i < 2 && (
                                <div className={`w-12 sm:w-20 h-0.5 mx-4 ${step > s.num ? 'bg-primary' : 'bg-secondary'}`} />
                            )}
                        </div>
                    ))}
                </div>

                {/* Step 1: Basic Info */}
                {step === 1 && (
                    <div className="glass rounded-xl p-8 max-w-xl mx-auto animate-fadeIn">
                        <h2 className="text-2xl font-bold mb-6">Basic Information</h2>
                        
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="title">Resume Title</Label>
                                <Input
                                    data-testid="resume-title-input"
                                    id="title"
                                    placeholder="My Gaming Resume"
                                    value={resume.title}
                                    onChange={(e) => setResume(prev => ({ ...prev, title: e.target.value }))}
                                    className="bg-black/50 border-white/10"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="target_job">Target Job (Optional)</Label>
                                <Input
                                    data-testid="target-job-input"
                                    id="target_job"
                                    placeholder="e.g., Project Manager, Team Lead"
                                    value={resume.target_job}
                                    onChange={(e) => setResume(prev => ({ ...prev, target_job: e.target.value }))}
                                    className="bg-black/50 border-white/10"
                                />
                                <p className="text-xs text-muted-foreground">This helps AI tailor skills to your desired role</p>
                            </div>
                            
                            <Button 
                                data-testid="step1-next-btn"
                                onClick={handleStep1Submit}
                                disabled={saving}
                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider py-6 btn-glow"
                            >
                                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Next: Add Games <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 2: Add Games */}
                {step === 2 && (
                    <div className="space-y-8 animate-fadeIn">
                        {/* Import from Steam */}
                        <div className="flex justify-end">
                            <Dialog open={steamDialogOpen} onOpenChange={setSteamDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button 
                                        data-testid="import-steam-btn"
                                        variant="outline" 
                                        className="border-white/10 hover:bg-white/5"
                                    >
                                        <Import className="w-4 h-4 mr-2" />
                                        Import from Steam
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="bg-card border-border">
                                    <DialogHeader>
                                        <DialogTitle>Import Steam Games</DialogTitle>
                                        <DialogDescription>
                                            Import your most played games from Steam. Your profile must be public.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 mt-4">
                                        <div className="space-y-2">
                                            <Label>Steam ID (64-bit)</Label>
                                            <Input
                                                data-testid="steam-id-input"
                                                placeholder="76561198xxxxxxxxx"
                                                value={steamId}
                                                onChange={(e) => setSteamId(e.target.value)}
                                                className="bg-black/50 border-white/10"
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Find your Steam ID at steamid.io
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Steam API Key (Optional)</Label>
                                            <Input
                                                data-testid="steam-api-key-input"
                                                placeholder="Your Steam API key"
                                                value={steamApiKey}
                                                onChange={(e) => setSteamApiKey(e.target.value)}
                                                className="bg-black/50 border-white/10"
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Get a key at steamcommunity.com/dev/apikey
                                            </p>
                                        </div>
                                        <Button 
                                            data-testid="confirm-import-steam-btn"
                                            onClick={importSteamGames}
                                            disabled={importingGames}
                                            className="w-full bg-primary"
                                        >
                                            {importingGames ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Importing...
                                                </>
                                            ) : (
                                                <>
                                                    <Import className="w-4 h-4 mr-2" />
                                                    Import Games
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                        {/* Add Game Form */}
                        <div className="glass rounded-xl p-8">
                            <h2 className="text-2xl font-bold mb-2">Add Your Games</h2>
                            <p className="text-muted-foreground mb-6">What video games do you want to write about?</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label>Game Name</Label>
                                    <Input
                                        data-testid="game-name-input"
                                        placeholder="e.g., League of Legends, Fortnite"
                                        value={currentGame.name}
                                        onChange={(e) => setCurrentGame(prev => ({ ...prev, name: e.target.value }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Platform</Label>
                                    <Select 
                                        value={currentGame.platform} 
                                        onValueChange={(value) => setCurrentGame(prev => ({ ...prev, platform: value }))}
                                    >
                                        <SelectTrigger data-testid="platform-select" className="bg-black/50 border-white/10">
                                            <SelectValue placeholder="Select platform" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PLATFORMS.map(p => (
                                                <SelectItem key={p.value} value={p.value}>
                                                    <div className="flex items-center gap-2">
                                                        <p.icon className="w-4 h-4" />
                                                        {p.label}
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Level / Rank</Label>
                                    <Input
                                        data-testid="level-input"
                                        placeholder="e.g., Diamond, Prestige Master"
                                        value={currentGame.level || currentGame.rank}
                                        onChange={(e) => setCurrentGame(prev => ({ ...prev, level: e.target.value, rank: e.target.value }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Hours Played</Label>
                                    <Input
                                        data-testid="hours-input"
                                        type="number"
                                        placeholder="e.g., 500"
                                        value={currentGame.hours_played}
                                        onChange={(e) => setCurrentGame(prev => ({ ...prev, hours_played: e.target.value }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                            </div>
                            
                            {/* Achievements */}
                            <div className="mt-6 space-y-3">
                                <Label>Achievements (Optional)</Label>
                                <div className="flex gap-2">
                                    <Input
                                        data-testid="achievement-input"
                                        placeholder="e.g., Victory Royale, Diamond Rank, Raid Leader"
                                        value={newAchievement}
                                        onChange={(e) => setNewAchievement(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && addAchievement()}
                                        className="bg-black/50 border-white/10"
                                    />
                                    <Button 
                                        data-testid="add-achievement-btn"
                                        onClick={addAchievement} 
                                        variant="outline" 
                                        className="border-white/10"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                
                                {currentGame.achievements.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        {currentGame.achievements.map((ach, i) => (
                                            <span 
                                                key={`achievement-${ach}-${i}`}
                                                className="inline-flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary text-sm rounded-full"
                                            >
                                                <Trophy className="w-3 h-3" />
                                                {ach}
                                                <button onClick={() => removeAchievement(i)} className="ml-1 hover:text-destructive">
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            
                            {/* Play Style Questionnaire */}
                            <div className="mt-6">
                                <button 
                                    type="button"
                                    onClick={() => setShowPlayStyleForm(!showPlayStyleForm)}
                                    className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {showPlayStyleForm ? 'Hide Play Style Details' : 'Want a stronger, more personalised result? Add 2 quick details (10 seconds)'}
                                </button>
                                
                                {showPlayStyleForm && (
                                    <div className="mt-4 p-4 bg-black/30 border border-white/10 rounded-lg space-y-4">
                                        <p className="text-xs text-muted-foreground">Help the AI understand HOW you played for more personalized skill translations.</p>
                                        
                                        {/* Core Gameplay Style */}
                                        <div className="space-y-3">
                                            <Label className="text-xs text-primary flex items-center gap-1">
                                                <Gamepad2 className="w-3 h-3" /> Core Gameplay Style
                                            </Label>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Mode</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.mode || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, mode: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="solo">Solo</SelectItem>
                                                            <SelectItem value="team">Team</SelectItem>
                                                            <SelectItem value="both">Both</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Playstyle</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.playstyle || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, playstyle: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="aggressive">Aggressive (fast decisions)</SelectItem>
                                                            <SelectItem value="strategic">Strategic (planning)</SelectItem>
                                                            <SelectItem value="supportive">Supportive (team help)</SelectItem>
                                                            <SelectItem value="balanced">Balanced</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Role (if team)</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.primary_role || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, primary_role: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="leader">Leader / Shot-caller</SelectItem>
                                                            <SelectItem value="support">Support / Utility</SelectItem>
                                                            <SelectItem value="damage">Damage / Execution</SelectItem>
                                                            <SelectItem value="flexible">Flexible</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Thinking & Approach */}
                                        <div className="space-y-3">
                                            <Label className="text-xs text-primary flex items-center gap-1">
                                                <Target className="w-3 h-3" /> Thinking & Approach
                                            </Label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Decision Style</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.decision_style || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, decision_style: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="fast-paced">Fast-paced decisions</SelectItem>
                                                            <SelectItem value="calculated">Calculated / analytical</SelectItem>
                                                            <SelectItem value="reactive">Reactive / adaptive</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Focus Area</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.focus_area || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, focus_area: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="positioning">Positioning / awareness</SelectItem>
                                                            <SelectItem value="resource_management">Resource management</SelectItem>
                                                            <SelectItem value="strategy">Strategy / planning</SelectItem>
                                                            <SelectItem value="mechanics">Mechanics / execution</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Challenge & Growth */}
                                        <div className="space-y-3">
                                            <Label className="text-xs text-primary flex items-center gap-1">
                                                <TrendingUp className="w-3 h-3" /> Challenge & Growth
                                            </Label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Biggest Challenge</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.biggest_challenge || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, biggest_challenge: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="opponents">Stronger opponents</SelectItem>
                                                            <SelectItem value="coordination">Team coordination</SelectItem>
                                                            <SelectItem value="resources">Resource limitations</SelectItem>
                                                            <SelectItem value="pressure">High-pressure situations</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">How You Improved</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.how_improved || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, how_improved: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="practice">Practice / repetition</SelectItem>
                                                            <SelectItem value="reviewing">Reviewing mistakes</SelectItem>
                                                            <SelectItem value="learning_strategies">Learning strategies</SelectItem>
                                                            <SelectItem value="adapting">Adapting playstyle</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Consistency & Effort */}
                                        <div className="space-y-3">
                                            <Label className="text-xs text-primary flex items-center gap-1">
                                                <BarChart3 className="w-3 h-3" /> Consistency & Effort
                                            </Label>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Play Frequency</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.play_frequency || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, play_frequency: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="casual">Casual</SelectItem>
                                                            <SelectItem value="regular">Regular</SelectItem>
                                                            <SelectItem value="competitive">Competitive grind</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Performance Style</Label>
                                                    <Select 
                                                        value={currentGame.play_style?.performance_style || ''} 
                                                        onValueChange={(v) => setCurrentGame(prev => ({...prev, play_style: {...prev.play_style, performance_style: v}}))}
                                                    >
                                                        <SelectTrigger className="bg-black/50 border-white/10 h-8 text-xs">
                                                            <SelectValue placeholder="Select..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="consistent">Consistent over time</SelectItem>
                                                            <SelectItem value="peak">High peak performance</SelectItem>
                                                            <SelectItem value="clutch">Clutch moments</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <Button 
                                data-testid="add-game-btn"
                                onClick={addGame}
                                className="mt-6 bg-secondary hover:bg-secondary/80 rounded-sm font-bold uppercase tracking-wider"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Game
                            </Button>
                        </div>

                        {/* Added Games List */}
                        {resume.games.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <Gamepad2 className="w-5 h-5 text-primary" />
                                    Added Games ({resume.games.length})
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {resume.games.map((game, index) => (
                                        <div 
                                            key={game.name ? `game-${game.name}-${game.platform}` : `game-${index}`}
                                            data-testid={`game-card-${index}`}
                                            className="bg-card border border-border rounded-lg p-4 flex items-start justify-between"
                                        >
                                            <div>
                                                <h4 className="font-bold">{game.name}</h4>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className={`platform-badge ${game.platform === 'PC' ? 'platform-pc' : game.platform === 'Console' ? 'platform-console' : 'platform-mobile'}`}>
                                                        {game.platform}
                                                    </span>
                                                    {game.level && <span className="text-sm text-muted-foreground">{game.level}</span>}
                                                    {game.hours_played && <span className="text-sm text-muted-foreground">{game.hours_played}h</span>}
                                                </div>
                                                {game.achievements?.length > 0 && (
                                                    <p className="text-xs text-muted-foreground mt-2">
                                                        {game.achievements.length} achievement(s)
                                                    </p>
                                                )}
                                            </div>
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                onClick={() => removeGame(index)}
                                                className="text-muted-foreground hover:text-destructive"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Navigation */}
                        <div className="flex justify-between gap-4">
                            <Button 
                                data-testid="step2-back-btn"
                                variant="outline" 
                                onClick={() => setStep(1)}
                                className="border-white/10"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                            <Button 
                                data-testid="generate-skills-btn"
                                onClick={handleStep2Submit}
                                disabled={generating || resume.games.length === 0}
                                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider btn-glow"
                            >
                                {generating ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Generating Skills...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="mr-2 w-4 h-4" />
                                        Generate Skills
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 3: Preview & Export */}
                {step === 3 && (
                    <div className="space-y-8 animate-fadeIn">
                        {/* Personal Information */}
                        <div className="glass rounded-xl p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <User className="w-5 h-5 text-primary" />
                                <h2 className="text-2xl font-bold">Personal Information</h2>
                            </div>
                            <p className="text-sm text-muted-foreground mb-6">This information will appear on your PDF resume for employers to contact you.</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-muted-foreground" />
                                        Full Name
                                    </Label>
                                    <Input
                                        data-testid="personal-full-name"
                                        placeholder="John Doe"
                                        value={resume.personal_info?.full_name || ''}
                                        onChange={(e) => setResume(prev => ({
                                            ...prev,
                                            personal_info: { ...prev.personal_info, full_name: e.target.value }
                                        }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-muted-foreground" />
                                        Email Address
                                    </Label>
                                    <Input
                                        data-testid="personal-email"
                                        type="email"
                                        placeholder="john@example.com"
                                        value={resume.personal_info?.email || ''}
                                        onChange={(e) => setResume(prev => ({
                                            ...prev,
                                            personal_info: { ...prev.personal_info, email: e.target.value }
                                        }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-muted-foreground" />
                                        Phone Number
                                    </Label>
                                    <Input
                                        data-testid="personal-phone"
                                        type="tel"
                                        placeholder="+1 (555) 123-4567"
                                        value={resume.personal_info?.phone || ''}
                                        onChange={(e) => setResume(prev => ({
                                            ...prev,
                                            personal_info: { ...prev.personal_info, phone: e.target.value }
                                        }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-muted-foreground" />
                                        Location
                                    </Label>
                                    <Input
                                        data-testid="personal-location"
                                        placeholder="New York, NY"
                                        value={resume.personal_info?.location || ''}
                                        onChange={(e) => setResume(prev => ({
                                            ...prev,
                                            personal_info: { ...prev.personal_info, location: e.target.value }
                                        }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                        Date of Birth
                                    </Label>
                                    <Input
                                        data-testid="personal-dob"
                                        type="date"
                                        value={resume.personal_info?.date_of_birth || ''}
                                        onChange={(e) => setResume(prev => ({
                                            ...prev,
                                            personal_info: { ...prev.personal_info, date_of_birth: e.target.value }
                                        }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Linkedin className="w-4 h-4 text-muted-foreground" />
                                        LinkedIn (Optional)
                                    </Label>
                                    <Input
                                        data-testid="personal-linkedin"
                                        placeholder="linkedin.com/in/johndoe"
                                        value={resume.personal_info?.linkedin || ''}
                                        onChange={(e) => setResume(prev => ({
                                            ...prev,
                                            personal_info: { ...prev.personal_info, linkedin: e.target.value }
                                        }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-muted-foreground" />
                                        Website (Optional)
                                    </Label>
                                    <Input
                                        data-testid="personal-website"
                                        placeholder="johndoe.com"
                                        value={resume.personal_info?.website || ''}
                                        onChange={(e) => setResume(prev => ({
                                            ...prev,
                                            personal_info: { ...prev.personal_info, website: e.target.value }
                                        }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Education Section */}
                        <div className="glass rounded-xl p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <GraduationCap className="w-5 h-5 text-primary" />
                                <h2 className="text-2xl font-bold">Education</h2>
                            </div>
                            <p className="text-sm text-muted-foreground mb-6">Add your educational background (optional but recommended).</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="space-y-2">
                                    <Label>Institution *</Label>
                                    <Input
                                        data-testid="edu-institution"
                                        placeholder="e.g., MIT, Stanford University"
                                        value={currentEducation.institution}
                                        onChange={(e) => setCurrentEducation(prev => ({ ...prev, institution: e.target.value }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Degree *</Label>
                                    <Input
                                        data-testid="edu-degree"
                                        placeholder="e.g., Bachelor of Science, High School Diploma"
                                        value={currentEducation.degree}
                                        onChange={(e) => setCurrentEducation(prev => ({ ...prev, degree: e.target.value }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Field of Study</Label>
                                    <Input
                                        data-testid="edu-field"
                                        placeholder="e.g., Computer Science, Business"
                                        value={currentEducation.field_of_study}
                                        onChange={(e) => setCurrentEducation(prev => ({ ...prev, field_of_study: e.target.value }))}
                                        className="bg-black/50 border-white/10"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="space-y-2">
                                        <Label>Start Year</Label>
                                        <Input
                                            data-testid="edu-start-year"
                                            type="number"
                                            placeholder="2020"
                                            value={currentEducation.start_year}
                                            onChange={(e) => setCurrentEducation(prev => ({ ...prev, start_year: e.target.value }))}
                                            className="bg-black/50 border-white/10"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>End Year</Label>
                                        <Input
                                            data-testid="edu-end-year"
                                            type="number"
                                            placeholder="2024"
                                            value={currentEducation.end_year}
                                            onChange={(e) => setCurrentEducation(prev => ({ ...prev, end_year: e.target.value }))}
                                            disabled={currentEducation.is_current}
                                            className="bg-black/50 border-white/10"
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2 mb-4">
                                <input
                                    type="checkbox"
                                    id="edu-current"
                                    checked={currentEducation.is_current}
                                    onChange={(e) => setCurrentEducation(prev => ({ ...prev, is_current: e.target.checked, end_year: '' }))}
                                    className="rounded"
                                />
                                <Label htmlFor="edu-current" className="cursor-pointer">Currently studying here</Label>
                            </div>
                            
                            {/* Education Achievements */}
                            <div className="space-y-2 mb-4">
                                <Label>Achievements / Activities (Optional)</Label>
                                <div className="flex gap-2">
                                    <Input
                                        data-testid="edu-achievement-input"
                                        placeholder="e.g., Dean's List, Club President"
                                        value={newEduAchievement}
                                        onChange={(e) => setNewEduAchievement(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && addEduAchievement()}
                                        className="bg-black/50 border-white/10"
                                    />
                                    <Button onClick={addEduAchievement} variant="outline" className="border-white/10">
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                {currentEducation.achievements.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {currentEducation.achievements.map((ach, i) => (
                                            <span key={`edu-ach-${ach.substring(0, 10)}-${i}`} className="inline-flex items-center gap-1 px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">
                                                {ach}
                                                <button onClick={() => removeEduAchievement(i)} className="ml-1 hover:text-destructive">
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            
                            <Button 
                                data-testid="add-education-btn"
                                onClick={addEducation}
                                className="bg-secondary hover:bg-secondary/80"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Education
                            </Button>
                            
                            {/* Added Education List */}
                            {resume.education?.length > 0 && (
                                <div className="mt-6 space-y-3">
                                    <h4 className="font-bold text-sm text-muted-foreground">Added Education ({resume.education.length})</h4>
                                    {resume.education.map((edu, index) => (
                                        <div key={edu.institution ? `edu-${edu.institution}-${edu.degree}` : `edu-${index}`} className="flex items-start justify-between p-4 bg-card border border-border rounded-lg">
                                            <div>
                                                <h5 className="font-bold">{edu.degree}{edu.field_of_study ? ` in ${edu.field_of_study}` : ''}</h5>
                                                <p className="text-sm text-muted-foreground">{edu.institution}</p>
                                                {(edu.start_year || edu.end_year) && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {edu.start_year}{edu.is_current ? ' - Present' : edu.end_year ? ` - ${edu.end_year}` : ''}
                                                    </p>
                                                )}
                                            </div>
                                            <Button variant="ghost" size="sm" onClick={() => removeEducation(index)} className="text-muted-foreground hover:text-destructive">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Share Settings */}
                        {resumeId && (
                            <div className="glass rounded-xl p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <Share2 className="w-5 h-5 text-primary" />
                                        <div>
                                            <h3 className="font-bold">Share Resume</h3>
                                            <p className="text-sm text-muted-foreground">Make your resume publicly accessible</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Switch
                                            data-testid="public-toggle"
                                            checked={resume.is_public}
                                            onCheckedChange={togglePublic}
                                        />
                                        {resume.is_public && (
                                            <Button 
                                                data-testid="copy-share-link-btn"
                                                variant="outline" 
                                                size="sm"
                                                onClick={copyShareLink}
                                                className="border-white/10"
                                            >
                                                <Copy className="w-4 h-4 mr-2" />
                                                Copy Link
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Job Tailoring - Premium Feature */}
                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <Wand2 className="w-5 h-5 text-yellow-500" />
                                    <div>
                                        <h3 className="font-bold flex items-center gap-2">
                                            Tailor to Job Posting
                                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                                                <Crown className="w-3 h-3" />
                                                PREMIUM
                                            </span>
                                        </h3>
                                        <p className="text-sm text-muted-foreground">AI rewrites your resume to match specific job requirements</p>
                                    </div>
                                </div>
                                {userTier === 'premium' ? (
                                    <Button 
                                        data-testid="open-tailor-btn"
                                        onClick={() => setTailorDialogOpen(true)}
                                        disabled={!resumeId || resume.skills.length === 0}
                                        className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                                        variant="outline"
                                    >
                                        <Wand2 className="w-4 h-4 mr-2" />
                                        Tailor Resume
                                    </Button>
                                ) : (
                                    <Button 
                                        data-testid="upgrade-for-tailor-btn"
                                        onClick={() => navigate('/pricing')}
                                        className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                                        variant="outline"
                                    >
                                        <Lock className="w-4 h-4 mr-2" />
                                        Upgrade to Unlock
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Cover Letter Generation - Premium Feature */}
                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <FileEdit className="w-5 h-5 text-yellow-500" />
                                    <div>
                                        <h3 className="font-bold flex items-center gap-2">
                                            Generate Cover Letter
                                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                                                <Crown className="w-3 h-3" />
                                                PREMIUM
                                            </span>
                                        </h3>
                                        <p className="text-sm text-muted-foreground">AI creates a tailored cover letter based on your resume</p>
                                    </div>
                                </div>
                                {userTier === 'premium' ? (
                                    <Button 
                                        data-testid="open-cover-letter-btn"
                                        onClick={() => setCoverLetterDialogOpen(true)}
                                        disabled={!resumeId || resume.skills.length === 0}
                                        className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                                        variant="outline"
                                    >
                                        <FileEdit className="w-4 h-4 mr-2" />
                                        Generate Letter
                                    </Button>
                                ) : (
                                    <Button 
                                        data-testid="upgrade-for-cover-letter-btn"
                                        onClick={() => navigate('/pricing')}
                                        className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                                        variant="outline"
                                    >
                                        <Lock className="w-4 h-4 mr-2" />
                                        Upgrade to Unlock
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Skill Gap Detector - Free+ */}
                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <AlertCircle className="w-5 h-5 text-green-500" />
                                    <div>
                                        <h3 className="font-bold flex items-center gap-2">
                                            Skill Gap Detector
                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                                FREE
                                            </span>
                                        </h3>
                                        <p className="text-sm text-muted-foreground">Analyze your resume for missing skills and improvement areas</p>
                                    </div>
                                </div>
                                <Button 
                                    data-testid="analyze-gaps-btn"
                                    onClick={() => { setSkillGapDialogOpen(true); analyzeSkillGaps(); }}
                                    disabled={!resumeId || resume.skills.length === 0}
                                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                                    variant="outline"
                                >
                                    <AlertCircle className="w-4 h-4 mr-2" />
                                    Analyze Gaps
                                </Button>
                            </div>
                        </div>

                        {/* Achievement Strength Scoring - Pro+ */}
                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <BarChart3 className="w-5 h-5 text-primary" />
                                    <div>
                                        <h3 className="font-bold flex items-center gap-2">
                                            Achievement Strength Scoring
                                            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                                                PRO
                                            </span>
                                        </h3>
                                        <p className="text-sm text-muted-foreground">Rate your achievements on Leadership, Analytical, Communication</p>
                                    </div>
                                </div>
                                {userTier !== 'free' ? (
                                    <Button 
                                        data-testid="score-achievements-btn"
                                        onClick={() => { setAchievementScoreDialogOpen(true); scoreAchievements(); }}
                                        disabled={!resumeId || !resume.games.some(g => g.achievements?.length > 0)}
                                        className="bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30"
                                        variant="outline"
                                    >
                                        <BarChart3 className="w-4 h-4 mr-2" />
                                        Score Achievements
                                    </Button>
                                ) : (
                                    <Button 
                                        data-testid="upgrade-for-scoring-btn"
                                        onClick={() => navigate('/pricing')}
                                        className="bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30"
                                        variant="outline"
                                    >
                                        <Lock className="w-4 h-4 mr-2" />
                                        Upgrade to Pro
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Interview Question Predictor - Premium */}
                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <HelpCircle className="w-5 h-5 text-yellow-500" />
                                    <div>
                                        <h3 className="font-bold flex items-center gap-2">
                                            Interview Question Predictor
                                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                                                <Crown className="w-3 h-3" />
                                                PREMIUM
                                            </span>
                                        </h3>
                                        <p className="text-sm text-muted-foreground">Predict likely interview questions based on a job posting</p>
                                    </div>
                                </div>
                                {userTier === 'premium' ? (
                                    <Button 
                                        data-testid="open-interview-btn"
                                        onClick={() => setInterviewDialogOpen(true)}
                                        disabled={!resumeId || resume.skills.length === 0}
                                        className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                                        variant="outline"
                                    >
                                        <HelpCircle className="w-4 h-4 mr-2" />
                                        Predict Questions
                                    </Button>
                                ) : (
                                    <Button 
                                        data-testid="upgrade-for-interview-btn"
                                        onClick={() => navigate('/pricing')}
                                        className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-500/30"
                                        variant="outline"
                                    >
                                        <Lock className="w-4 h-4 mr-2" />
                                        Upgrade to Premium
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Job Recommendations - All Tiers */}
                        <div className="glass rounded-xl p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <TrendingUp className="w-5 h-5 text-green-500" />
                                    <div>
                                        <h3 className="font-bold flex items-center gap-2">
                                            Job Recommendations
                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                                FREE
                                            </span>
                                        </h3>
                                        <p className="text-sm text-muted-foreground">Get job suggestions based on your skills with salary estimates</p>
                                    </div>
                                </div>
                                <Button 
                                    data-testid="get-job-recs-btn"
                                    onClick={() => { setJobRecsDialogOpen(true); getJobRecommendations(); }}
                                    disabled={!resumeId || resume.skills.length === 0}
                                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                                    variant="outline"
                                >
                                    <TrendingUp className="w-4 h-4 mr-2" />
                                    Get Recommendations
                                </Button>
                            </div>
                        </div>

                        {/* Resume Tone Selector */}
                        <div className="glass rounded-xl p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <Sparkles className="w-5 h-5 text-primary" />
                                <h2 className="text-2xl font-bold">Resume Tone</h2>
                            </div>
                            <p className="text-sm text-muted-foreground mb-6">
                                Select a tone to change how your skills and summary are written. Changing tone will regenerate your content.
                            </p>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {availableTones.map((tone) => (
                                    <button
                                        key={tone.id}
                                        data-testid={`tone-${tone.id}`}
                                        onClick={() => tone.available && handleToneChange(tone.id)}
                                        disabled={generating}
                                        className={`relative p-4 rounded-lg border text-left transition-all ${
                                            selectedTone === tone.id
                                                ? 'border-primary bg-primary/10'
                                                : tone.available
                                                    ? 'border-border hover:border-primary/50 cursor-pointer'
                                                    : 'border-border opacity-60 cursor-not-allowed'
                                        }`}
                                    >
                                        {!tone.available && (
                                            <div className="absolute top-2 right-2">
                                                <Lock className="w-3 h-3 text-muted-foreground" />
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`font-bold text-sm ${!tone.available ? 'text-muted-foreground' : ''}`}>
                                                {tone.name}
                                            </span>
                                            {tone.tier_required !== 'free' && (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                    tone.tier_required === 'premium' 
                                                        ? 'bg-yellow-500/20 text-yellow-400'
                                                        : 'bg-primary/20 text-primary'
                                                }`}>
                                                    {tone.tier_required.toUpperCase()}
                                                </span>
                                            )}
                                        </div>
                                        <p className={`text-xs ${tone.available ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}>
                                            {tone.description}
                                        </p>
                                        {selectedTone === tone.id && (
                                            <div className="absolute top-2 right-2">
                                                <Check className="w-4 h-4 text-primary" />
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                            
                            {userTier === 'free' && (
                                <p className="text-xs text-muted-foreground mt-4 flex items-center gap-2">
                                    <Lock className="w-3 h-3" />
                                    <span>Upgrade to <button onClick={() => navigate('/pricing')} className="text-primary hover:underline">Pro or Premium</button> to unlock more tones</span>
                                </p>
                            )}
                        </div>

                        {/* Skills Section */}
                        <div className="glass rounded-xl p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold">Generated Skills</h2>
                                <Button 
                                    data-testid="regenerate-skills-btn"
                                    variant="outline"
                                    onClick={generateSkills}
                                    disabled={generating}
                                    className="border-white/10"
                                >
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                                    Regenerate
                                </Button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {resume.skills.map((skill, index) => (
                                    <div 
                                        key={skill.professional_skill ? `skill-${skill.professional_skill}` : `skill-${index}`}
                                        data-testid={`skill-card-${index}`}
                                        className="bg-card border border-border rounded-lg p-4"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-primary/20 rounded-lg shrink-0">
                                                <Target className="w-4 h-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">{skill.professional_skill}</h4>
                                                <p className="text-xs text-muted-foreground mt-1">{skill.description}</p>
                                                <p className="text-xs text-primary/70 mt-2 font-mono">From: {skill.gaming_skill}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Summary Section */}
                        <div className="glass rounded-xl p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold">Professional Summary</h2>
                                <Button 
                                    data-testid="generate-summary-btn"
                                    variant="outline"
                                    onClick={generateSummary}
                                    disabled={generating}
                                    className="border-white/10"
                                >
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                                    {resume.summary ? 'Regenerate' : 'Generate'} Summary
                                </Button>
                            </div>
                            
                            {resume.summary ? (
                                <div className="bg-card border border-border rounded-lg p-6">
                                    <p className="text-muted-foreground leading-relaxed">{resume.summary}</p>
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-center py-8">
                                    Click "Generate Summary" to create a professional summary based on your skills
                                </p>
                            )}
                        </div>

                        {/* Template Selection */}
                        <div className="glass rounded-xl p-6">
                            <h3 className="font-bold mb-4">Resume Template</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {TEMPLATES.map(t => (
                                    <button
                                        key={t.value}
                                        data-testid={`template-${t.value}`}
                                        onClick={() => setSelectedTemplate(t.value)}
                                        className={`p-4 rounded-lg border text-left transition-all ${
                                            selectedTemplate === t.value 
                                                ? 'border-primary bg-primary/10' 
                                                : 'border-border hover:border-primary/50'
                                        }`}
                                    >
                                        <h4 className="font-bold">{t.label}</h4>
                                        <p className="text-xs text-muted-foreground">{t.description}</p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row justify-between gap-4">
                            <Button 
                                data-testid="step3-back-btn"
                                variant="outline" 
                                onClick={() => setStep(2)}
                                className="border-white/10"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Edit Games
                            </Button>
                            
                            <div className="flex gap-4">
                                <Button 
                                    data-testid="save-resume-btn"
                                    variant="outline"
                                    onClick={() => saveResume()}
                                    disabled={saving}
                                    className="border-white/10"
                                >
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                                    Save
                                </Button>
                                <Button 
                                    data-testid="download-pdf-btn"
                                    onClick={downloadPDF}
                                    disabled={!resumeId || downloadingPdf}
                                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider btn-glow"
                                >
                                    {downloadingPdf ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4 mr-2" />
                                            Download PDF
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Feedback Dialog - shown after PDF download */}
            <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
                <DialogContent className="bg-card border-border max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Target className="w-5 h-5 text-primary" />
                            Did this resume help?
                        </DialogTitle>
                        <DialogDescription>
                            Your feedback helps us improve and show accurate success rates to other users.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-3 mt-4">
                        <Button
                            data-testid="feedback-interview-btn"
                            onClick={() => submitFeedback('got_interview')}
                            disabled={feedbackSubmitting}
                            className="justify-start bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30"
                            variant="outline"
                        >
                            <ThumbsUp className="w-4 h-4 mr-3" />
                            Yes, I got an interview!
                        </Button>
                        <Button
                            data-testid="feedback-job-btn"
                            onClick={() => submitFeedback('got_job')}
                            disabled={feedbackSubmitting}
                            className="justify-start bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30"
                            variant="outline"
                        >
                            <Trophy className="w-4 h-4 mr-3" />
                            Yes, I got the job!
                        </Button>
                        <Button
                            data-testid="feedback-none-btn"
                            onClick={() => submitFeedback('none')}
                            disabled={feedbackSubmitting}
                            variant="ghost"
                            className="justify-start text-muted-foreground"
                        >
                            <ThumbsDown className="w-4 h-4 mr-3" />
                            Not yet / Skip
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Job Tailoring Dialog - Premium Feature */}
            <Dialog open={tailorDialogOpen} onOpenChange={setTailorDialogOpen}>
                <DialogContent className="bg-card border-border max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Wand2 className="w-5 h-5 text-primary" />
                            Tailor Resume to Job
                            <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full flex items-center gap-1">
                                <Crown className="w-3 h-3" />
                                PREMIUM
                            </span>
                        </DialogTitle>
                        <DialogDescription>
                            Paste a job description and our AI will rewrite your skills and summary to match the requirements.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {!tailorResult ? (
                        <div className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Job Description</Label>
                                <Textarea
                                    data-testid="job-description-input"
                                    placeholder="Paste the full job posting here..."
                                    value={jobDescription}
                                    onChange={(e) => setJobDescription(e.target.value)}
                                    className="bg-black/50 border-white/10 min-h-[200px]"
                                />
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setTailorDialogOpen(false)}
                                    className="border-white/10"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    data-testid="tailor-submit-btn"
                                    onClick={tailorToJob}
                                    disabled={tailoring || !jobDescription.trim()}
                                    className="bg-primary"
                                >
                                    {tailoring ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Analyzing...
                                        </>
                                    ) : (
                                        <>
                                            <Wand2 className="w-4 h-4 mr-2" />
                                            Tailor Resume
                                        </>
                                    )}
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4 mt-4">
                            {/* Match Score */}
                            <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg border border-primary/30">
                                <span className="font-bold">Match Score</span>
                                <span className="text-2xl font-bold text-primary">{tailorResult.match_score}%</span>
                            </div>
                            
                            {/* Matched Keywords */}
                            <div>
                                <Label className="text-sm text-muted-foreground">Keywords Matched</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {tailorResult.matched_keywords?.slice(0, 8).map((kw, i) => (
                                        <span key={`keyword-${kw}`} className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                            {kw}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Preview of changes */}
                            <div className="max-h-[200px] overflow-y-auto p-3 bg-black/30 rounded-lg text-sm">
                                <p className="text-muted-foreground mb-2">New Summary:</p>
                                <p className="text-foreground">{tailorResult.tailored_summary}</p>
                            </div>
                            
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setTailorResult(null);
                                        setJobDescription('');
                                    }}
                                    className="border-white/10"
                                >
                                    Try Different Job
                                </Button>
                                <Button
                                    data-testid="apply-tailored-btn"
                                    onClick={applyTailoredContent}
                                    className="bg-primary"
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    Apply Changes
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Cover Letter Dialog */}
            <Dialog open={coverLetterDialogOpen} onOpenChange={(open) => !open && resetCoverLetterDialog()}>
                <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileEdit className="w-5 h-5 text-yellow-500" />
                            Generate Cover Letter
                        </DialogTitle>
                        <DialogDescription>
                            Create a professional cover letter tailored to your skills and target role.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {!coverLetterResult ? (
                        <div className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Company Name (Optional)</Label>
                                <Input
                                    data-testid="cover-letter-company"
                                    placeholder="e.g., Acme Corp"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="bg-black/50 border-white/10"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Hiring Manager Name (Optional)</Label>
                                <Input
                                    data-testid="cover-letter-manager"
                                    placeholder="e.g., Jane Smith"
                                    value={hiringManager}
                                    onChange={(e) => setHiringManager(e.target.value)}
                                    className="bg-black/50 border-white/10"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Job Description (Optional)</Label>
                                <Textarea
                                    data-testid="cover-letter-job-desc"
                                    placeholder="Paste the job posting to make the cover letter more targeted..."
                                    value={coverLetterJobDesc}
                                    onChange={(e) => setCoverLetterJobDesc(e.target.value)}
                                    className="bg-black/50 border-white/10 min-h-[150px]"
                                />
                                <p className="text-xs text-muted-foreground">Adding a job description helps AI tailor the cover letter specifically</p>
                            </div>
                            
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={resetCoverLetterDialog}
                                    className="border-white/10"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    data-testid="generate-cover-letter-btn"
                                    onClick={generateCoverLetter}
                                    disabled={generatingCoverLetter}
                                    className="bg-primary"
                                >
                                    {generatingCoverLetter ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Generate Cover Letter
                                        </>
                                    )}
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4 mt-4">
                            {/* Key Highlights */}
                            <div>
                                <Label className="text-sm text-muted-foreground">Key Highlights</Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {coverLetterResult.key_highlights?.map((highlight, i) => (
                                        <span key={`highlight-${highlight.substring(0, 15)}`} className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                                            {highlight}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Cover Letter Text */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Your Cover Letter</Label>
                                    <span className="text-xs text-muted-foreground">{coverLetterResult.word_count} words</span>
                                </div>
                                <div className="bg-black/50 border border-white/10 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                                    <p className="text-sm whitespace-pre-line leading-relaxed">{coverLetterResult.cover_letter}</p>
                                </div>
                            </div>
                            
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setCoverLetterResult(null)}
                                    className="border-white/10"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Regenerate
                                </Button>
                                <Button
                                    data-testid="copy-cover-letter-btn"
                                    onClick={copyCoverLetter}
                                    className="bg-primary"
                                >
                                    <Copy className="w-4 h-4 mr-2" />
                                    Copy to Clipboard
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Skill Gap Dialog */}
            <Dialog open={skillGapDialogOpen} onOpenChange={setSkillGapDialogOpen}>
                <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] flex flex-col">
                    <DialogHeader className="shrink-0">
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-green-500" />
                            Skill Gap Analysis
                        </DialogTitle>
                        <DialogDescription>
                            Understanding your strengths and areas for improvement.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto pr-2">
                        {analyzingGaps ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                                <p className="text-muted-foreground">Analyzing your skills...</p>
                            </div>
                        ) : skillGapResult ? (
                            <div className="space-y-4 mt-4">
                                {/* Summary */}
                                <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                                    <p className="text-sm">{skillGapResult.summary}</p>
                                </div>
                                
                                {/* Strengths */}
                                <div>
                                    <Label className="text-green-400 flex items-center gap-2 mb-2">
                                        <Check className="w-4 h-4" />
                                        Strengths
                                    </Label>
                                    <div className="space-y-2">
                                        {skillGapResult.strengths?.map((s, i) => (
                                            <div key={`strength-${s.skill}`} className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                                                <p className="font-bold text-sm text-green-400">{s.skill}</p>
                                                <p className="text-xs text-muted-foreground">{s.evidence}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Gaps */}
                                <div>
                                    <Label className="text-orange-400 flex items-center gap-2 mb-2">
                                        <AlertCircle className="w-4 h-4" />
                                        Areas to Improve
                                    </Label>
                                    <div className="space-y-2">
                                        {skillGapResult.gaps?.map((g, i) => (
                                            <div key={`gap-${g.skill}`} className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                                                <p className="font-bold text-sm text-orange-400">{g.skill}</p>
                                                <p className="text-xs text-muted-foreground">{g.suggestion}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Tip */}
                                <div className="p-3 bg-muted rounded-lg">
                                    <p className="text-xs text-muted-foreground"><strong>Tip:</strong> {skillGapResult.improvement_tip}</p>
                                </div>
                            </div>
                        ) : null}
                    </div>
                    
                    <DialogFooter className="shrink-0 pt-4 border-t border-border mt-4">
                        <Button variant="outline" onClick={() => setSkillGapDialogOpen(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Achievement Score Dialog */}
            <Dialog open={achievementScoreDialogOpen} onOpenChange={setAchievementScoreDialogOpen}>
                <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Achievement Strength Scores
                        </DialogTitle>
                        <DialogDescription>
                            Your achievements rated on key professional competencies.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {scoringAchievements ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                            <p className="text-muted-foreground">Scoring your achievements...</p>
                        </div>
                    ) : achievementScoreResult ? (
                        <div className="space-y-4 mt-4">
                            {/* Overall Profile */}
                            {achievementScoreResult.overall_profile && (
                                <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                                    <div className="grid grid-cols-3 gap-4 mb-3">
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-primary">{achievementScoreResult.overall_profile.leadership_avg?.toFixed(1)}</p>
                                            <p className="text-xs text-muted-foreground">Leadership</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-green-400">{achievementScoreResult.overall_profile.analytical_avg?.toFixed(1)}</p>
                                            <p className="text-xs text-muted-foreground">Analytical</p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-yellow-400">{achievementScoreResult.overall_profile.communication_avg?.toFixed(1)}</p>
                                            <p className="text-xs text-muted-foreground">Communication</p>
                                        </div>
                                    </div>
                                    <p className="text-xs text-center text-muted-foreground">{achievementScoreResult.overall_profile.summary}</p>
                                </div>
                            )}
                            
                            {/* Individual Achievements */}
                            <div className="space-y-3">
                                {achievementScoreResult.scored_achievements?.map((ach, i) => (
                                    <div key={`scored-${ach.achievement?.substring(0, 20)}-${ach.game}`} className="p-4 bg-card border border-border rounded-lg">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <p className="font-bold text-sm">{ach.achievement}</p>
                                                <p className="text-xs text-muted-foreground">{ach.game}</p>
                                            </div>
                                            <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                                                {ach.top_strength}: {ach.scores?.[ach.top_strength?.toLowerCase()]?.toFixed(1)}/10
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 mt-3">
                                            <div className="text-center p-2 bg-muted rounded">
                                                <p className="font-bold text-sm">{ach.scores?.leadership?.toFixed(1)}</p>
                                                <p className="text-[10px] text-muted-foreground">Leadership</p>
                                            </div>
                                            <div className="text-center p-2 bg-muted rounded">
                                                <p className="font-bold text-sm">{ach.scores?.analytical?.toFixed(1)}</p>
                                                <p className="text-[10px] text-muted-foreground">Analytical</p>
                                            </div>
                                            <div className="text-center p-2 bg-muted rounded">
                                                <p className="font-bold text-sm">{ach.scores?.communication?.toFixed(1)}</p>
                                                <p className="text-[10px] text-muted-foreground">Communication</p>
                                            </div>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-2">{ach.brief_reason}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAchievementScoreDialogOpen(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Interview Question Predictor Dialog */}
            <Dialog open={interviewDialogOpen} onOpenChange={(open) => !open && resetInterviewDialog()}>
                <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <HelpCircle className="w-5 h-5 text-yellow-500" />
                            Interview Question Predictor
                        </DialogTitle>
                        <DialogDescription>
                            Predict interview questions based on a specific job posting.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {!interviewResult ? (
                        <div className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Paste the Job Description</Label>
                                <Textarea
                                    data-testid="interview-job-desc"
                                    placeholder="Paste the full job posting here to predict what questions they'll ask..."
                                    value={interviewJobDesc}
                                    onChange={(e) => setInterviewJobDesc(e.target.value)}
                                    className="bg-black/50 border-white/10 min-h-[200px]"
                                />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={resetInterviewDialog} className="border-white/10">
                                    Cancel
                                </Button>
                                <Button
                                    data-testid="predict-interview-btn"
                                    onClick={predictInterviewQuestions}
                                    disabled={predictingInterview || !interviewJobDesc.trim()}
                                    className="bg-primary"
                                >
                                    {predictingInterview ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Predicting...
                                        </>
                                    ) : (
                                        <>
                                            <HelpCircle className="w-4 h-4 mr-2" />
                                            Predict Questions
                                        </>
                                    )}
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <div className="space-y-4 mt-4">
                            {/* Key Themes */}
                            <div className="flex flex-wrap gap-2">
                                {interviewResult.key_themes?.map((theme, i) => (
                                    <span key={`theme-${theme}`} className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                                        {theme}
                                    </span>
                                ))}
                            </div>
                            
                            {/* Preparation Tips */}
                            <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                                <Label className="text-yellow-400 mb-2 block">Preparation Tips</Label>
                                <ul className="space-y-1">
                                    {interviewResult.preparation_tips?.map((tip, i) => (
                                        <li key={`tip-${tip.substring(0, 20)}`} className="text-sm text-muted-foreground flex items-start gap-2">
                                            <span className="text-yellow-400">•</span>
                                            {tip}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            
                            {/* Predicted Questions */}
                            <div className="space-y-3">
                                <Label>Predicted Questions ({interviewResult.question_count})</Label>
                                {interviewResult.predicted_questions?.map((q, i) => (
                                    <div key={`question-${q.question?.substring(0, 30)}`} className="p-4 bg-card border border-border rounded-lg">
                                        <div className="flex items-start justify-between mb-2">
                                            <p className="font-bold text-sm flex-1">{q.question}</p>
                                            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-[10px] rounded ml-2 shrink-0">
                                                {q.category}
                                            </span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mb-2">{q.why_likely}</p>
                                        <p className="text-xs text-primary"><strong>Suggested angle:</strong> {q.suggested_angle}</p>
                                    </div>
                                ))}
                            </div>
                            
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setInterviewResult(null)} className="border-white/10">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    New Prediction
                                </Button>
                                <Button onClick={resetInterviewDialog} className="bg-primary">
                                    Done
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Job Recommendations Dialog */}
            <Dialog open={jobRecsDialogOpen} onOpenChange={setJobRecsDialogOpen}>
                <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-500" />
                            Job Recommendations
                        </DialogTitle>
                        <DialogDescription>
                            Jobs that match your gaming-derived skills with salary estimates.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {loadingJobRecs ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                            <p className="text-muted-foreground">Analyzing your skills and finding matching jobs...</p>
                        </div>
                    ) : jobRecsResult ? (
                        <div className="space-y-4 mt-4">
                            {/* Achievement Summary */}
                            <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                                <p className="text-sm">{jobRecsResult.achievement_summary}</p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {jobRecsResult.top_skills?.map((skill, i) => (
                                        <span key={`topskill-${skill}`} className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Recommended Jobs */}
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2">
                                    <Briefcase className="w-4 h-4" />
                                    Recommended Jobs ({jobRecsResult.recommended_jobs?.length})
                                </Label>
                                {jobRecsResult.recommended_jobs?.map((job, i) => (
                                    <div key={`job-${job.title}`} className="p-4 bg-card border border-border rounded-lg">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <p className="font-bold">{job.title}</p>
                                                <p className="text-xs text-muted-foreground">{job.industry}</p>
                                            </div>
                                            <span className="px-2 py-1 bg-green-500/20 text-green-400 text-sm rounded-full font-bold">
                                                {job.match_score}% match
                                            </span>
                                        </div>
                                        
                                        {/* Salary Range */}
                                        <div className="flex items-center gap-2 my-3 p-2 bg-muted rounded">
                                            <DollarSign className="w-4 h-4 text-green-400" />
                                            <span className="font-bold text-green-400">
                                                ${job.salary_range?.min?.toLocaleString()} - ${job.salary_range?.max?.toLocaleString()}
                                            </span>
                                            <span className="text-xs text-muted-foreground">/year</span>
                                        </div>
                                        
                                        <p className="text-xs text-muted-foreground mb-2">{job.why_good_fit}</p>
                                        
                                        <div className="flex flex-wrap gap-1">
                                            {job.skills_used?.map((skill, j) => (
                                                <span key={j} className="px-2 py-0.5 bg-muted text-muted-foreground text-[10px] rounded">
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Career Insight */}
                            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                                <p className="text-xs text-yellow-400"><strong>Career Insight:</strong> {jobRecsResult.career_insight}</p>
                            </div>
                        </div>
                    ) : null}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setJobRecsDialogOpen(false)} className="border-white/10">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
